"use client";

import { useState } from "react";

interface ProjectionTableProps {
  headers: string[];
  rows: Array<Array<string | number>>;
  title?: string;
  pageSize?: number;
}

export default function ProjectionTable({
  headers,
  rows,
  title,
  pageSize = 10,
}: ProjectionTableProps) {
  const [currentPage, setCurrentPage] = useState(1);

  if (!rows || rows.length === 0) {
    return <div className="empty-state">No data available</div>;
  }

  const totalPages = Math.ceil(rows.length / pageSize);
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = Math.min(startIndex + pageSize, rows.length);
  const currentRows = rows.slice(startIndex, endIndex);

  return (
    <div className="projection-table-container anim-fade-in">
      {title && <h4 className="table-title">{title}</h4>}
      
      <div className="table-wrapper">
        <table className="projection-table">
          <thead>
            <tr>
              {headers.map((h, i) => (
                <th key={i} className={i === 0 ? "text-left" : "text-right"}>
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {currentRows.map((row, rIdx) => (
              <tr key={rIdx} className="table-row">
                {row.map((val, cIdx) => (
                  <td
                    key={cIdx}
                    className={cIdx === 0 ? "text-left font-semibold" : "text-right font-medium"}
                  >
                    {typeof val === "number" ? val.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : val}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="pagination-controls">
          <button
            onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
            disabled={currentPage === 1}
            className="pagination-btn"
          >
            &larr; Prev
          </button>
          <span className="pagination-info">
            Page {currentPage} of {totalPages} (Rows {startIndex + 1}-{endIndex} of {rows.length})
          </span>
          <button
            onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
            disabled={currentPage === totalPages}
            className="pagination-btn"
          >
            Next &rarr;
          </button>
        </div>
      )}

      <style jsx>{`
        .projection-table-container {
          width: 100%;
          max-width: 100%;
          min-width: 0;
          overflow: hidden;
          display: flex;
          flex-direction: column;
          gap: var(--space-4);
          background-color: var(--bg-surface);
          border: 1px solid var(--border-color);
          border-radius: var(--radius-md);
          padding: var(--space-5);
          box-sizing: border-box;
        }
        @media (max-width: 640px) {
          .projection-table-container {
            padding: var(--space-3);
          }
        }
        .table-title {
          font-family: var(--font-heading);
          font-size: var(--text-sm);
          font-weight: var(--fw-extrabold);
          color: var(--text-secondary);
          text-transform: uppercase;
          letter-spacing: 0.05em;
          margin-bottom: var(--space-2);
          border-bottom: 1px solid var(--border-color);
          padding-bottom: var(--space-3);
          width: 100%;
        }
        .table-wrapper {
          width: 100%;
          max-width: 100%;
          min-width: 0;
          overflow-x: auto;
          -webkit-overflow-scrolling: touch;
          box-sizing: border-box;
          border-radius: var(--radius-sm);
          border: 1px solid var(--border-color);
        }
        .projection-table {
          width: 100%;
          min-width: 540px;
          border-collapse: collapse;
          text-align: left;
          font-family: var(--font-body);
          font-size: var(--text-sm);
        }
        @media (max-width: 480px) {
          .projection-table {
            font-size: var(--text-xs);
            min-width: 420px;
          }
        }
        th {
          background-color: var(--bg-surface-hover);
          color: var(--text-muted);
          font-family: var(--font-heading);
          font-size: var(--text-xs);
          font-weight: var(--fw-extrabold);
          text-transform: uppercase;
          letter-spacing: 0.05em;
          padding: var(--space-3) var(--space-4);
          border-bottom: 2px solid var(--border-color);
          white-space: nowrap;
        }
        td {
          padding: var(--space-3) var(--space-4);
          border-bottom: 1px solid var(--border-color);
          color: var(--text-primary);
          white-space: nowrap;
        }
        @media (max-width: 480px) {
          th, td { padding: var(--space-2) var(--space-3); }
        }
        .table-row:nth-child(even) td {
          background-color: var(--overlay-a01);
        }
        :global(.light-mode) .table-row:nth-child(even) td {
          background-color: var(--shadow-black-a02);
        }
        .table-row:hover td {
          background-color: var(--bg-surface-hover);
        }
        .text-left {
          text-align: left;
        }
        .text-right {
          text-align: right;
        }
        .pagination-controls {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding-top: var(--space-2);
          flex-wrap: wrap;
          gap: var(--space-3);
        }
        .pagination-btn {
          background-color: var(--bg-input);
          border: 1px solid var(--border-color);
          color: var(--text-primary);
          padding: var(--space-2) var(--space-4);
          border-radius: var(--radius-sm);
          font-size: var(--text-sm);
          font-weight: var(--fw-semibold);
          cursor: pointer;
          transition: all var(--transition-fast);
        }
        .pagination-btn:hover:not(:disabled) {
          background-color: var(--bg-surface-hover);
          border-color: var(--text-muted);
        }
        .pagination-btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }
        .pagination-info {
          font-size: var(--text-sm);
          color: var(--text-secondary);
          font-weight: var(--fw-medium);
        }
        .empty-state {
          padding: var(--space-8);
          text-align: center;
          color: var(--text-muted);
          border: 1px dashed var(--border-color);
          border-radius: var(--radius-md);
        }
      `}</style>
    </div>
  );
}
